import classNames from 'classnames';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { PeopleApi } from '../../../api';
import type { AddressResponse } from '../../../model/Address';
import { AddressAutocomplete, Modal } from '../../molecules';

type FormData = AddressResponse & {
  address: AddressResponse | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  patientId: number | null;
  initialData?: AddressResponse | null;
  onAddressSelect: (address: AddressResponse) => void;
};

export const CreateAddressModal = ({
  isOpen,
  onClose,
  onAddressSelect,
  patientId,
  initialData = null,
}: Props) => {
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      address: null,
      address_text: '',
      index: '',
      country_name: '',
      region_name: '',
      city_name: '',
      street_name: '',
      house: '',
      flat: '',
      people_main_id: patientId ?? null,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      reset({
        address: initialData,
        address_text: initialData.address_text || '',
        index: initialData.index || '',
        country_name: initialData.country_name || '',
        region_name: initialData.region_name || '',
        city_name: initialData.city_name || '',
        street_name: initialData.street_name || '',
        house: initialData.house || '',
        flat: initialData.flat || '',
        people_main_id: initialData.people_main_id ?? patientId,
      });
    } else {
      reset({
        address: null,
        address_text: '',
        index: '',
        country_name: '',
        region_name: '',
        city_name: '',
        street_name: '',
        house: '',
        flat: '',
        people_main_id: patientId ?? null,
      });
    }
  }, [isOpen, initialData, patientId, reset]);

  const watchedFields = watch([
    'region_name',
    'city_name',
    'street_name',
    'house',
    'flat',
  ]);

  useEffect(() => {
    const [region_name, city_name, street_name, house, flat] = watchedFields;
    const formattedSource = [
      region_name,
      city_name,
      street_name,
      house ? `д. ${house}` : '',
      flat ? `кв. ${flat}` : '',
    ]
      .filter(Boolean)
      .join(', ');

    setValue('address_text', formattedSource);

    const currentAddress = getValues('address');

    setValue('address', {
      id: currentAddress?.id || 0,
      address_text: formattedSource,
      index: currentAddress?.index || '',
      country_name: currentAddress?.country_name || '',
      region_name: region_name || '',
      city_name: city_name || '',
      street_name: street_name || '',
      house: house || '',
      flat: flat || '',
      people_main_id: currentAddress?.people_main_id ?? patientId ?? null,
    });
  }, [watchedFields, setValue, getValues, patientId]);

  const {
    data: addressList = { total: 0, items: [] },
    isLoading: isAddressesLoading,
  } = useQuery({
    queryKey: [PeopleApi.KEY, 'getAddress', patientId],
    queryFn: () => PeopleApi.getAddress(patientId),
    enabled: false,
  });

  const { mutateAsync: createAddress } = useMutation({
    mutationFn: (data: AddressResponse) =>
      PeopleApi.createPatientsAddress(data),
    onSuccess: (response) => {
      toast.success('Адрес успешно добавлен');
      queryClient.invalidateQueries({
        queryKey: [PeopleApi.KEY, 'getAddress', patientId],
      });
      onAddressSelect(response.data);
      reset();
      onClose();
    },
    onError: () => {
      toast.error('Ошибка при сохранении адреса. Проверьте данные');
    },
  });

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (!patientId) {
        toast.error('Не указан ID пациента');
        return;
      }

      if (data.address && data.address.id !== 0) {
        onAddressSelect(data.address);
        onClose();
        return;
      }

      const addressToSend: AddressResponse = {
        ...data,
        people_main_id: patientId,
      };
      await createAddress(addressToSend);
    },
    [createAddress, patientId, onAddressSelect, onClose],
  );

  const onAddressChange = (address: AddressResponse) => {
    setValue('address', address);
    setValue('address_text', address.address_text || '');
    setValue('region_name', address.region_name || '');
    setValue('city_name', address.city_name || '');
    setValue('street_name', address.street_name || '');
    setValue('house', address.house || '');
    setValue('flat', address.flat || '');
  };

  // bg-white, а не bg-[#F1F7FA]: у модалки такой же фон, и чипы на нём
  // сливались — выглядели как текст без подложки.
  const radioStyles =
    'max-w-full px-[12px] py-[4px] rounded-[8px] bg-white transition-colors cursor-pointer has-[input:checked]:bg-[#151515] has-[input:checked]:text-white';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-h-[90vh] min-h-[300px] flex-col overflow-visible rounded-[8px] bg-[#F1F7FA] p-[24px]">
        {/* pr-[40px] — чтобы верхний ряд чипов не уезжал под крестик модалки */}
        {!isAddressesLoading && addressList.items.length > 0 && (
          <div className="mb-[16px] pr-[40px] pt-[24px]">
            <div>
              <div className="flex flex-wrap gap-[12px] pb-[8px]">
                {addressList.items.map((addr) => {
                  const label =
                    [
                      addr.city_name,
                      addr.street_name,
                      addr.house ? `д. ${addr.house}` : '',
                      addr.flat ? `кв. ${addr.flat}` : '',
                    ]
                      .filter(Boolean)
                      .join(', ') || addr.address_text;
                  if (!label) return null;
                  return (
                    <label key={addr.id} className={radioStyles}>
                      <input
                        className="hidden"
                        type="radio"
                        name="saved-address"
                        onChange={() => onAddressChange(addr)}
                        checked={getValues('address')?.id === addr.id}
                      />
                      <span className="leading-normal">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex grow flex-col pt-[24px]"
        >
          <div className="md-[4px] mb-[12px] w-full overflow-visible">
            <Controller
              control={control}
              name="address"
              rules={{
                required: true,
              }}
              render={({ field }) => {
                return (
                  <AddressAutocomplete
                    value={field.value}
                    onChange={(address) => {
                      field.onChange(address);
                      onAddressChange(address);
                    }}
                  />
                );
              }}
            />
            {errors.address && (
              <p className="text-red-600 mt-[4px] text-sm">
                {errors.address.message}
              </p>
            )}
          </div>

          <div className="mb-[24px] grid max-h-[50vh] grid-cols-1 gap-[12px] overflow-y-auto sm:grid-cols-2">
            <input
              {...register('region_name', { required: true })}
              className={classNames(
                'flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-[#afc4d1] px-[20px] py-[18px] outline-none transition hover:border-[#0788f1] focus-within:border-[#0788f1] focus-within:border-[1.6px]',
                errors.region_name && 'border-red',
              )}
              placeholder="Регион"
            />
            <input
              {...register('city_name', { required: true })}
              className={classNames(
                'flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-[#afc4d1] px-[20px] py-[18px] outline-none transition hover:border-[#0788f1] focus-within:border-[#0788f1] focus-within:border-[1.6px]',
                errors.region_name && 'border-red',
              )}
              placeholder="Город"
            />
            <input
              {...register('street_name', { required: true })}
              className={classNames(
                'flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-[#afc4d1] px-[20px] py-[18px] outline-none transition hover:border-[#0788f1] focus-within:border-[#0788f1] focus-within:border-[1.6px]',
                errors.region_name && 'border-red',
              )}
              placeholder="Улица"
            />
            <input
              {...register('house', { required: true })}
              className={classNames(
                'flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-[#afc4d1] px-[20px] py-[18px] outline-none transition hover:border-[#0788f1] focus-within:border-[#0788f1] focus-within:border-[1.6px]',
                errors.region_name && 'border-red',
              )}
              placeholder="Дом"
            />
            <input
              {...register('flat', { required: true })}
              className={classNames(
                'flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-[#afc4d1] px-[20px] py-[18px] outline-none transition hover:border-[#0788f1] focus-within:border-[#0788f1] focus-within:border-[1.6px]',
                errors.region_name && 'border-red',
              )}
              placeholder="Квартира"
            />
          </div>

          <button
            className="mt-[24px] flex h-[60px] w-full items-center justify-center rounded-[12px] bg-[#0788f1] p-[20px] text-base font-bold text-white"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Сохраняем...' : 'Добавить адрес'}
          </button>
        </form>
      </div>
    </Modal>
  );
};
